import createInfoBarMessageViewState from './createInfoBarMessageViewState';
import { temporarilyShowContentAction } from './createSafetyBarMessageViewState';
import { safetyBarLinkTemporarilyMarkAsSafe } from './createSafetyBarMessageViewState.locstring.json';
import initializeCustomAction from './initializeCustomAction';
import { isFeatureEnabled } from 'owa-feature-flags';
import removeInfoBarMessage from 'owa-info-bar/lib/actions/removeInfoBarMessage';
import loc, { format } from 'owa-localize';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import { ClientItem, InstrumentationContext } from 'owa-mail-store';
import { lazyBlockLastSenderFromReadingPane } from 'owa-mail-triage-action';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import Message from 'owa-service/lib/contract/Message';
import MessageSafetyLevel from 'owa-service/lib/contract/MessageSafetyLevel';
import MessageSafetyReason from 'owa-service/lib/contract/MessageSafetyReason';
import {
    externalSender,
    blockedContent,
    manageSender,
    blockSender,
} from './createExternalMessageInfobar.locstring.json';
import {
    InfoBarMessageRank,
    InfoBarMessageViewState,
    InfoBarCustomAction,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { externalInOutlookEnabled } from 'owa-nonboot-userconfiguration-manager';

export default function createExternalMessageInfobar(
    message: Message,
    key: string,
    itemViewState: ItemViewState,
    instrumentationContexts: InstrumentationContext[]
): InfoBarMessageViewState {
    if (
        isFeatureEnabled('rp-externalIconAndLabel') &&
        message?.IsExternalSender &&
        externalInOutlookEnabled()
    ) {
        const emailAddress = message.From?.Mailbox?.EmailAddress
            ? message.From.Mailbox.EmailAddress
            : '';
        const displayText = format(loc(externalSender), emailAddress);
        const messageParts: (string | InfoBarCustomAction)[] = [displayText];
        const infobarMessageViewState: InfoBarMessageViewState = createInfoBarMessageViewState(
            key,
            messageParts,
            InfoBarMessageRank.External
        );
        if (
            message.MessageSafety?.MessageSafetyReason &&
            message.MessageSafety.MessageSafetyReason !== MessageSafetyReason.PartialContentDisabled
        ) {
            // When message has MessageSafetyReason anything other than partial content disabled,
            // we leave the safety infobar as it is and add an additional infobar to indicate external sender.
            // One such example is external messages in junk folder.
            return infobarMessageViewState;
        }

        // The order of messageParts as per design is:
        // 1. External sender information.
        // 2. Blocked content information. (if has blocked content)
        // 3. Safety custom action i.e. 'Block sender' or 'Manage sender'.
        // 4. Show blocked content custom action. (if has blocked content)
        const showBlockedContentAction: InfoBarCustomAction = initializeCustomAction();
        const safetyCustomAction: InfoBarCustomAction = initializeCustomAction();

        if (message.MessageSafety || message.HasBlockedImages) {
            // Remove safety infobar as it contains redundant information about blocked content.
            removeInfoBarMessage(itemViewState, 'safetyBar');
            messageParts.push(loc(blockedContent));
            showBlockedContentAction.text = loc(safetyBarLinkTemporarilyMarkAsSafe);
            showBlockedContentAction.action = temporarilyShowContentAction(
                message,
                key,
                itemViewState
            );
        }

        if (isSafeSender(message)) {
            safetyCustomAction.text = loc(manageSender);
            safetyCustomAction.action = manageSenderAction();
        } else {
            safetyCustomAction.text = loc(blockSender);
            safetyCustomAction.action = blockSenderAction(
                message as ClientItem,
                instrumentationContexts?.[0]
            );
        }

        messageParts.push(safetyCustomAction);
        if (showBlockedContentAction.text && showBlockedContentAction.action) {
            messageParts.push('|');
            messageParts.push(showBlockedContentAction);
        }

        infobarMessageViewState.messageParts = messageParts;
        return infobarMessageViewState;
    }
    return null;
}

function isSafeSender(message: Message): boolean {
    return (
        message &&
        !message.BlockStatus &&
        (!message.MessageSafety ||
            !message.MessageSafety.MessageSafetyLevel ||
            message.MessageSafety.MessageSafetyLevel <= MessageSafetyLevel.Safe)
    );
}

const blockSenderAction = (
    message: ClientItem,
    instrumentationContext: InstrumentationContext
) => () => {
    lazyBlockLastSenderFromReadingPane.importAndExecute(
        message.ItemId.Id,
        [instrumentationContext],
        'ReadingPane'
    );
};

const manageSenderAction = () => () => {
    lazyMountAndShowFullOptions.importAndExecute('mail', 'junkEmail');
};
